import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ECollection } from '../enums/ECollection';
import { IQueryFormat } from '../enums/types';
import { ITicketMessages, ITicketMessages_P, ITicketMessages_S } from '../interfaces/ITicketMessages';
import { BaseService } from './base-service';
import { UserService } from './user.service';
import { UtilsService } from './utils.service';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class TicketMessagesService extends BaseService {
    constructor(
        public override http: HttpClient,
        public override utilsService: UtilsService,
        public override userService: UserService
    ) {
        super(http, userService, utilsService, `/schema/${environment.USER_PATH}/${environment.INS}/${environment.DB}/${ECollection.SW_TICKETS_MESSAGES}`);
    }

    /**
     * Create a new message in a ticket
     */
    async createMessage(messageData: any): Promise<ITicketMessages> {
        return await this.post<ITicketMessages>(messageData);
    }

    /**
     * Get all messages for a specific ticket (issue)
     */
    async getTicketMessages(issueId: string, select?: ITicketMessages_S): Promise<ITicketMessages[]> {
        const query = <IQueryFormat>{
            find: { issue_id: issueId },
            select: select || {
                message_id: 1,
                issue_id: 1,
                sender_type: 1,
                sender_id: 1,
                message: 1,
                createdAt: 1
            },
            sort: { createdAt: 1 } // Ascending order - oldest first
        };
        return await this.query<ITicketMessages[]>(query);
    }

    /**
     * Get messages with sender information (deep query)
     */
    async getTicketMessagesWithSender(issueId: string): Promise<ITicketMessages[]> {
        const query = <IQueryFormat>{
            find: { issue_id: issueId },
            select: {
                message_id: 1,
                issue_id: 1,
                sender_type: 1,
                sender_id: 1,
                message: 1,
                createdAt: 1
            },
            sort: { createdAt: 1 }
        };
        return await this.query<ITicketMessages[]>(query);
    }

    /**
     * Get count of unread messages (messages after a certain date)
     */
    async getUnreadMessageCount(issueId: string, afterDate?: Date): Promise<number> {
        const find: any = { issue_id: issueId, active: true };
        if (afterDate) {
            find.createdAt = { $gt: afterDate };
        }
        const query = <IQueryFormat>{
            find: find
        };
        return await this.countFromQuery(query);
    }

    /**
     * Soft delete a message
     */
    async deleteMessage(messageId: string): Promise<ITicketMessages> {
        return await this.softDelete<ITicketMessages>(messageId);
    }

    /**
     * Get single message by ID
     */
    async getMessageById(messageId: string): Promise<ITicketMessages> {
        return await this.getById<ITicketMessages>(messageId);
    }
}
